import { openDB, type IDBPDatabase } from 'idb'
import type { Message, Chat } from '../types'

const DB_NAME = 'ggram-cache'
const DB_VERSION = 1
const MAX_MESSAGES_PER_CHAT = 200
const SYNC_TS_KEY = 'ggram_chat_sync_ts'

interface GgramDB {
  messages: {
    key: string
    value: Message & { chat_id: string }
    indexes: { byChatId: string; byChatAndTime: [string, string] }
  }
  chats: {
    key: string
    value: Chat
  }
}

let db: IDBPDatabase<GgramDB> | null = null

async function getDB(): Promise<IDBPDatabase<GgramDB>> {
  if (db) return db
  db = await openDB<GgramDB>(DB_NAME, DB_VERSION, {
    upgrade(database) {
      if (!database.objectStoreNames.contains('messages')) {
        const msgStore = database.createObjectStore('messages', { keyPath: 'id' })
        msgStore.createIndex('byChatId', 'chat_id')
        msgStore.createIndex('byChatAndTime', ['chat_id', 'created_at'])
      }
      if (!database.objectStoreNames.contains('chats')) {
        database.createObjectStore('chats', { keyPath: 'id' })
      }
    },
  })
  return db
}

export const messageCache = {
  async saveMessages(chatId: string, messages: Message[]): Promise<void> {
    if (messages.length === 0) return
    try {
      const database = await getDB()
      const tx = database.transaction('messages', 'readwrite')
      await Promise.all(messages.map((m) => tx.store.put({ ...m, chat_id: chatId })))
      await tx.done
      // Prune to MAX_MESSAGES_PER_CHAT after saving
      await this.pruneMessages(chatId)
    } catch { /* cache is optional */ }
  },

  async mergeMessages(chatId: string, newMsgs: Message[]): Promise<void> {
    if (newMsgs.length === 0) return
    try {
      const database = await getDB()
      const tx = database.transaction('messages', 'readwrite')
      await Promise.all(newMsgs.map((m) => tx.store.put({ ...m, chat_id: chatId })))
      await tx.done
      await this.pruneMessages(chatId)
    } catch { /* cache is optional */ }
  },

  async pruneMessages(chatId: string): Promise<void> {
    try {
      const database = await getDB()
      const all = await database.getAllFromIndex('messages', 'byChatId', chatId)
      if (all.length <= MAX_MESSAGES_PER_CHAT) return
      // Sort ascending and delete the oldest excess
      all.sort((a, b) => a.created_at.localeCompare(b.created_at))
      const toDelete = all.slice(0, all.length - MAX_MESSAGES_PER_CHAT)
      const tx = database.transaction('messages', 'readwrite')
      await Promise.all(toDelete.map((m) => tx.store.delete(m.id)))
      await tx.done
    } catch { /* silently fail */ }
  },

  async getMessages(chatId: string): Promise<Message[]> {
    try {
      const database = await getDB()
      return database.getAllFromIndex('messages', 'byChatId', chatId)
    } catch {
      return []
    }
  },

  async getLatestMessageAt(chatId: string): Promise<string | null> {
    try {
      const msgs = await this.getMessages(chatId)
      if (msgs.length === 0) return null
      return msgs.reduce((latest, m) =>
        m.created_at > latest ? m.created_at : latest,
        msgs[0].created_at,
      )
    } catch {
      return null
    }
  },

  async saveChats(chats: Chat[]): Promise<void> {
    if (chats.length === 0) return
    try {
      const database = await getDB()
      const tx = database.transaction('chats', 'readwrite')
      await Promise.all(chats.map((c) => tx.store.put(c)))
      await tx.done
    } catch { /* silently fail */ }
  },

  async getChats(): Promise<Chat[]> {
    try {
      const database = await getDB()
      return database.getAll('chats')
    } catch {
      return []
    }
  },

  async updateUnread(chatId: string, count: number): Promise<void> {
    try {
      const database = await getDB()
      const chat = await database.get('chats', chatId)
      if (chat) {
        await database.put('chats', { ...chat, unread_count: count })
      }
    } catch { /* silently fail */ }
  },

  async deleteChat(chatId: string): Promise<void> {
    try {
      const database = await getDB()
      await database.delete('chats', chatId)
      const keys = await database.getAllKeysFromIndex('messages', 'byChatId', chatId)
      const tx = database.transaction('messages', 'readwrite')
      await Promise.all(keys.map((k) => tx.store.delete(k)))
      await tx.done
    } catch { /* silently fail */ }
  },

  async clearChat(chatId: string): Promise<void> {
    try {
      const database = await getDB()
      const keys = await database.getAllKeysFromIndex('messages', 'byChatId', chatId)
      const tx = database.transaction('messages', 'readwrite')
      await Promise.all(keys.map((k) => tx.store.delete(k)))
      await tx.done
    } catch { /* silently fail */ }
  },

  getSyncTimestamp(): string | null {
    return localStorage.getItem(SYNC_TS_KEY)
  },

  setSyncTimestamp(iso: string): void {
    localStorage.setItem(SYNC_TS_KEY, iso)
  },
}
